/**
 * `useSettings` — TanStack Query hook that resolves the unified
 * `SiteSettings` document (site name, tagline, logo, favicon,
 * homepage assignment, footer, contact info, social links, and
 * `siteOrigin`) through the typed `api/settings` module.
 *
 * The hook is a thin wrapper: it composes `getSettings` with the
 * deterministic query key produced by `hooks/keys.ts` and defers most
 * caching decisions (`gcTime`, retry policy, network mode) to the
 * shared `QueryClient` instance installed at the app root by
 * `providers/AppProviders.tsx` (Req 5.1, 5.2).
 *
 * The single override is `staleTime`: site-wide settings change
 * rarely (typically only on a Customizer save), so a 10-minute
 * freshness window keeps Navbar / Footer / SEOHead flicker-free
 * across route transitions and avoids re-fetching the same document
 * on every mount. The shared client's short default `staleTime`
 * would otherwise trigger a background refetch on every mount, which
 * is wasteful for a document that primes the layout chrome.
 *
 * All HTTP traffic flows through `api/settings` → `api/client.ts`;
 * the hook itself never imports `axios`, so replacing the transport
 * is a one-file change in `api/client.ts`.
 *
 * Returned value:
 *   - The raw `UseQueryResult<SiteSettings>` from TanStack Query so
 *     callers can pattern-match on `isLoading`, `error`, `data`, and
 *     `refetch` per the design's state-management conventions.
 *
 * Requirements: 5.1, 5.2, 2.2.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getSettings } from '@/api/settings';
import type { SiteSettings } from '@/types';

import { keys } from './keys';

/**
 * Freshness window for the `SiteSettings` document.
 *
 * Ten minutes is comfortably longer than any single navigation
 * session, so back-to-back route transitions serve the cached
 * document instead of re-fetching it. A Customizer save on the CMS
 * side still invalidates on the next `staleTime` boundary or via an
 * explicit `queryClient.invalidateQueries({ queryKey: keys.settings() })`.
 */
const SETTINGS_STALE_TIME_MS = 10 * 60 * 1000;

/**
 * Subscribe to the unified {@link SiteSettings} document.
 *
 * @returns The raw `UseQueryResult` so consumers can access `data`,
 *          `isLoading`, `error`, `refetch`, etc.
 */
export function useSettings(): UseQueryResult<SiteSettings> {
  return useQuery({
    queryKey: keys.settings(),
    queryFn: getSettings,
    staleTime: SETTINGS_STALE_TIME_MS,
  });
}
